// ============================================================================
// module-edit.ts — manuelle Modulbearbeitung (Levasoft-Parität #8)
// ----------------------------------------------------------------------------
// Reine Logik für das interaktive Löschen/Wiederherstellen einzelner Module der
// automatischen Belegung. Die Auto-Belegung (roof-belegung.ts) bleibt die Quelle
// der Wahrheit; manuelle Eingriffe werden als Menge deaktivierter Modul-IDs
// darübergelegt. So bleibt jede Eingabeänderung (Modulgröße/Dachform) konsistent,
// ohne dass eine zweite Geometrie gepflegt werden muss.
//
// Rein/deterministisch: keine DB, kein State, kein Zufall.
// ============================================================================
import type { ModuleRectLocal } from "./roof-belegung.js";

/** Ergebnis einer manuell überlagerten Belegung. */
export interface ManualLayoutSummary {
  /** Module, die nach Abzug der deaktivierten übrig bleiben. */
  enabled: ModuleRectLocal[];
  /** Module, die der Planer manuell entfernt hat (für die ausgegraute Darstellung). */
  disabled: ModuleRectLocal[];
  /** Anzahl aktiver Module. */
  count: number;
  /** installierte Leistung der aktiven Module in kWp. */
  kwp: number;
  /** Anzahl manuell entfernter Module (für UI-Hinweis „n entfernt"). */
  removedCount: number;
}

/**
 * Teilt die Auto-Belegung anhand der deaktivierten IDs in aktive/deaktivierte
 * Module und berechnet Anzahl + kWp der aktiven neu. `disabledIds`, die zu keinem
 * aktuellen Modul passen (z. B. nach Modulgrößen-Wechsel), werden ignoriert —
 * stale Einträge können die Belegung also nicht verfälschen.
 */
export function summarizeManualLayout(
  modules: ModuleRectLocal[],
  disabledIds: ReadonlySet<string>,
  wpPerModule: number,
): ManualLayoutSummary {
  const enabled: ModuleRectLocal[] = [];
  const disabled: ModuleRectLocal[] = [];
  for (const m of modules) {
    if (disabledIds.has(m.id)) disabled.push(m);
    else enabled.push(m);
  }
  const count = enabled.length;
  const kwp = Math.round(((count * Math.max(0, wpPerModule)) / 1000) * 1000) / 1000;
  return { enabled, disabled, count, kwp, removedCount: disabled.length };
}

/** Schaltet eine Modul-ID in der Deaktiviert-Menge um (immutabel). */
export function toggleDisabledModule(disabledIds: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(disabledIds);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
